// Separating a signature from the paper it was photographed on.
//
// Used from two places: automatically over the signature prefilled from the
// applicant's signaturePhotoUrl (see cleanSignatureImage at the foot of this
// file, called from PanFormDialog), and on demand when the operator ticks
// "Ink only" in the crop workspace.
//
// Nothing here uploads anything: the result is a base64 PNG embedded straight
// into the HTML posted to /pan-form/add, so the applicant's stored file is
// never modified.

/**
 * Remove ONLY the paper behind a photographed signature / thumb impression.
 * The ink is left exactly as it was photographed — same colour, same light and
 * dark, soft edges and the faint ridges of a thumb intact. Returns a NEW canvas
 * of the same size (the source is not touched), or null when there is no ink
 * to separate.
 *
 * A phone photo's paper is never one shade: it is darker in a corner, has a
 * shadow across it, a tint from the room light. One paper level for the whole
 * image left those parts behind as a grey / green haze. So the paper is
 * measured AREA BY AREA:
 *
 *  1. The image is cut into small blocks and each block's brightest colour is
 *     taken — in any block that is (almost) always paper, since strokes and a
 *     thumb's ridges have paper between them. Spreading each value over its
 *     neighbours (a max filter) covers blocks that are solid ink, and a blur
 *     smooths the result into a background map.
 *  2. Every pixel is divided by the paper colour behind it, so paper — shadow
 *     or not — becomes pure white and the ink keeps its own look relative to
 *     its paper.
 *  3. "Colour to alpha" against white: each pixel becomes the see-through
 *     colour that, printed on white, looks exactly like it did. Paper ends
 *     fully transparent.
 */
export const removePaperCanvas = (
  source: HTMLCanvasElement
): HTMLCanvasElement | null => {
  const ctx = source.getContext("2d");
  if (!ctx) return null;
  const w = source.width;
  const h = source.height;
  const n = w * h;
  const img = ctx.getImageData(0, 0, w, h); // may throw — caller catches
  const d = img.data;

  // Block grid. ~40 blocks along the long side: small enough to follow a
  // shadow's edge, big enough that each block reaches paper.
  const bs = Math.max(6, Math.round(Math.max(w, h) / 40));
  const bw = Math.ceil(w / bs);
  const bh = Math.ceil(h / bs);
  const nb = bw * bh;

  // Per block: the 90th-percentile-bright pixel's colour rather than the very
  // brightest, so a glare spot or a single hot pixel does not set the paper.
  const blk = new Float32Array(nb * 3);
  const lumBuf: number[] = [];
  for (let by = 0; by < bh; by += 1) {
    for (let bx = 0; bx < bw; bx += 1) {
      lumBuf.length = 0;
      const x1 = Math.min(w, (bx + 1) * bs);
      const y1 = Math.min(h, (by + 1) * bs);
      for (let y = by * bs; y < y1; y += 1) {
        for (let x = bx * bs; x < x1; x += 1) {
          const i = (y * w + x) * 4;
          if (d[i + 3] < 32) continue; // already transparent
          lumBuf.push(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
          lumBuf.push(i);
        }
      }
      const b = (by * bw + bx) * 3;
      const count = lumBuf.length / 2;
      if (!count) {
        blk[b] = blk[b + 1] = blk[b + 2] = 255;
        continue;
      }
      // Pick the pixel at the 90th percentile of brightness.
      const order = Array.from({ length: count }, (_, k) => k);
      order.sort((a, c) => lumBuf[a * 2] - lumBuf[c * 2]);
      const pick = lumBuf[order[Math.min(count - 1, Math.floor(count * 0.9))] * 2 + 1];
      blk[b] = d[pick];
      blk[b + 1] = d[pick + 1];
      blk[b + 2] = d[pick + 2];
    }
  }

  // A block that is solid ink (the dark core of a thumb print) has no paper of
  // its own, so it borrows the brightest of its 5x5 neighbourhood. Only such
  // blocks — clearly darker than their surroundings — borrow: a block that is
  // just shadowed paper keeps its own value, otherwise brighter paper next to
  // a shadow would be used inside it and leave the shadow's edge behind as a
  // band.
  const INK_BLOCK = 0.8;
  const spread = new Float32Array(nb * 3);
  const R = 2;
  for (let by = 0; by < bh; by += 1) {
    for (let bx = 0; bx < bw; bx += 1) {
      let best = -1;
      let bi = 0;
      for (let yy = Math.max(0, by - R); yy <= Math.min(bh - 1, by + R); yy += 1) {
        for (let xx = Math.max(0, bx - R); xx <= Math.min(bw - 1, bx + R); xx += 1) {
          const k = (yy * bw + xx) * 3;
          const l = 0.299 * blk[k] + 0.587 * blk[k + 1] + 0.114 * blk[k + 2];
          if (l > best) {
            best = l;
            bi = k;
          }
        }
      }
      const o = (by * bw + bx) * 3;
      const own = 0.299 * blk[o] + 0.587 * blk[o + 1] + 0.114 * blk[o + 2];
      const src = own >= best * INK_BLOCK ? o : bi;
      spread[o] = blk[src];
      spread[o + 1] = blk[src + 1];
      spread[o + 2] = blk[src + 2];
    }
  }

  // 3x3 box blur so neighbouring blocks blend instead of showing as tiles.
  const bg = new Float32Array(nb * 3);
  for (let by = 0; by < bh; by += 1) {
    for (let bx = 0; bx < bw; bx += 1) {
      let r = 0, g = 0, b = 0, c = 0;
      for (let yy = Math.max(0, by - 1); yy <= Math.min(bh - 1, by + 1); yy += 1) {
        for (let xx = Math.max(0, bx - 1); xx <= Math.min(bw - 1, bx + 1); xx += 1) {
          const k = (yy * bw + xx) * 3;
          r += spread[k];
          g += spread[k + 1];
          b += spread[k + 2];
          c += 1;
        }
      }
      const o = (by * bw + bx) * 3;
      bg[o] = r / c;
      bg[o + 1] = g / c;
      bg[o + 2] = b / c;
    }
  }

  // Not enough contrast to call anything ink (a blank slip, a black frame).
  let paperLum = 0;
  for (let k = 0; k < nb; k += 1) {
    paperLum += 0.299 * bg[k * 3] + 0.587 * bg[k * 3 + 1] + 0.114 * bg[k * 3 + 2];
  }
  paperLum /= nb;
  if (paperLum < 90) return null;

  // Paper's own grain and faint shading sit a few levels under the measured
  // paper; treating that much as paper too makes it vanish cleanly.
  const GRAIN = 0.94;
  let inkPixels = 0;
  for (let y = 0; y < h; y += 1) {
    // Bilinear lookup between block centres.
    const fy = Math.max(0, Math.min(bh - 1, (y + 0.5) / bs - 0.5));
    const y0 = Math.floor(fy);
    const y1 = Math.min(bh - 1, y0 + 1);
    const ty = fy - y0;
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4;
      if (d[i + 3] < 32) {
        d[i + 3] = 0;
        continue;
      }
      const fx = Math.max(0, Math.min(bw - 1, (x + 0.5) / bs - 0.5));
      const x0 = Math.floor(fx);
      const x1 = Math.min(bw - 1, x0 + 1);
      const tx = fx - x0;
      let a = 0;
      const rgb = [0, 0, 0];
      for (let c = 0; c < 3; c += 1) {
        const p =
          (bg[(y0 * bw + x0) * 3 + c] * (1 - tx) + bg[(y0 * bw + x1) * 3 + c] * tx) * (1 - ty) +
          (bg[(y1 * bw + x0) * 3 + c] * (1 - tx) + bg[(y1 * bw + x1) * 3 + c] * tx) * ty;
        const v = Math.min(255, (d[i + c] * 255) / Math.max(1, p * GRAIN));
        rgb[c] = v;
        a = Math.max(a, (255 - v) / 255);
      }
      if (a <= 0) {
        d[i + 3] = 0;
        continue;
      }
      d[i] = Math.round(255 - (255 - rgb[0]) / a);
      d[i + 1] = Math.round(255 - (255 - rgb[1]) / a);
      d[i + 2] = Math.round(255 - (255 - rgb[2]) / a);
      d[i + 3] = Math.round(a * 255);
      if (d[i + 3] > 64) inkPixels += 1;
    }
  }
  if (inkPixels < Math.max(25, n * 0.0005)) return null;

  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const outCtx = out.getContext("2d");
  if (!outCtx) return null;
  outCtx.putImageData(img, 0, 0);
  return out;
};

/**
 * Remove the paper (see removePaperCanvas) and trim the result to the ink's
 * own bounding box. Returns a NEW canvas, or null when there is no ink.
 *
 * Trimmed because the form's signature box is wide and short: an untrimmed
 * near-square photo is scaled down to the box's HEIGHT and the signature
 * prints small, floating in the middle of empty space.
 */
export const inkOnlyCanvas = (
  source: HTMLCanvasElement
): HTMLCanvasElement | null => {
  const clean = removePaperCanvas(source);
  if (!clean) return null;
  const ctx = clean.getContext("2d");
  if (!ctx) return null;
  const w = clean.width;
  const h = clean.height;
  const d = ctx.getImageData(0, 0, w, h).data;

  // Ink per row and per column. Counting instead of just taking min/max means
  // a speck of dust or a stray pen mark cannot drag the crop out to the edge.
  const rows = new Uint32Array(h);
  const cols = new Uint32Array(w);
  for (let p = 0, i = 0; p < w * h; p += 1, i += 4) {
    if (d[i + 3] > 64) {
      const x = p % w;
      rows[(p - x) / w] += 1;
      cols[x] += 1;
    }
  }
  const firstAbove = (counts: Uint32Array, from: number, to: number, step: number) => {
    for (let k = from; step > 0 ? k <= to : k >= to; k += step) {
      if (counts[k] >= 2) return k;
    }
    return -1;
  };
  const minY = firstAbove(rows, 0, h - 1, 1);
  const maxY = firstAbove(rows, h - 1, 0, -1);
  const minX = firstAbove(cols, 0, w - 1, 1);
  const maxX = firstAbove(cols, w - 1, 0, -1);
  if (minY < 0 || minX < 0) return null;

  // A hair of space around the strokes so nothing sits flush against the
  // printed box's rule.
  const pad = Math.max(2, Math.round(Math.max(maxX - minX, maxY - minY) * 0.02));
  const sx = Math.max(0, minX - pad);
  const sy = Math.max(0, minY - pad);
  const sw = Math.min(w, maxX + pad + 1) - sx;
  const sh = Math.min(h, maxY + pad + 1) - sy;

  const out = document.createElement("canvas");
  out.width = sw;
  out.height = sh;
  const outCtx = out.getContext("2d");
  if (!outCtx) return null;
  outCtx.drawImage(clean, sx, sy, sw, sh, 0, 0, sw, sh);
  return out;
};

/** Longest side of a cleaned signature, in px — keeps the posted HTML small. */
const SIGNATURE_MAX = 700;

/**
 * Load an image (a remote URL or a data-URL), separate its ink and trim it, and
 * return the result as a base64 PNG data-URL.
 *
 * Resolves to "" when the image cannot be loaded or holds no ink to separate —
 * the caller then keeps whatever it already had, so a signature that this pass
 * cannot improve still prints, and the operator can fix it by hand with the
 * Crop / Adjust controls.
 *
 * Remote URLs are requested with CORS (the API serves uploads with
 * Access-Control-Allow-Origin) so the canvas stays readable.
 */
export const cleanSignatureImage = (src: string): Promise<string> =>
  new Promise((resolve) => {
    if (!src) {
      resolve("");
      return;
    }
    const img = new Image();
    if (!src.startsWith("data:")) img.crossOrigin = "anonymous";
    img.onerror = () => resolve("");
    img.onload = () => {
      try {
        const scale = Math.min(1, SIGNATURE_MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve("");
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const cleaned = inkOnlyCanvas(canvas);
        resolve(cleaned ? cleaned.toDataURL("image/png") : "");
      } catch {
        // Tainted canvas (a remote image served without CORS) — nothing to do
        // here; the original image is still printable as-is.
        resolve("");
      }
    };
    img.src = src;
  });
